import React, { useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';

import useStores from '@stores/useStores';

const HOME_PATH = '/';
const DEFAULT_MESSAGE = 'Resource not found or insufficient privileges. Redirecting to homepage.';

export const RedirectRoute = ({
  to = HOME_PATH,
  message = DEFAULT_MESSAGE,
}: {
  to?: string;
  message?: string;
}) => {
  const { messagesStore } = useStores();
  const { pathname } = useLocation();

  // Redirecting to the current URL renders nothing and re-matches the same
  // routes, so the page would sit blank behind a toast claiming it moved.
  // Compared on the path alone, because a target may carry a `?tab=` query that
  // does not change which route matches.
  const isSelfRedirect = (target: string) => target.split('?')[0] === pathname;
  const destination = isSelfRedirect(to) ? HOME_PATH : to;

  // The app root is the fallback, so a gate ON the root has nowhere to send the
  // visitor. Rendering <Navigate> anyway is the blank pane this guard exists to
  // prevent, so render nothing and make the misconfiguration visible instead.
  const hasNowhereToGo = isSelfRedirect(destination);

  useEffect(() => {
    if (hasNowhereToGo) {
      // eslint-disable-next-line no-console
      console.error(
        `RedirectRoute: cannot redirect away from "${pathname}" - its target resolves to itself. ` +
          'A route gated at the app root needs an explicit `to` elsewhere.',
      );
      return;
    }

    messagesStore.showInfoMessage(message);
  }, []);

  if (hasNowhereToGo) {
    return null;
  }

  return <Navigate to={destination} replace />;
};
